import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-app-home>` says about itself.
 *
 * This is a whole SURFACE rather than a part of one. Everything on it — hero, the waiting list, tile
 * strip, breakdown panel, activity list — comes out of one property, `data`, and the component
 * decides only how those figures look. It holds no vocabulary of its own: it does not know what a
 * business calls its records, which objects have a lifecycle, or what any of them are worth. That is
 * why the tiles arrive as a LIST rather than as fixed slots, and why every figure arrives ALREADY
 * WORDED — a workspace with no orders should not be shown an "Open orders" tile reading zero.
 *
 * CAOS-684 — it is two regions, and the order is load-bearing. "What is waiting on you" leads; the
 * dashboard follows under "How things stand". An app's home says what you need to go and DO, and a
 * dashboard reports what already happened, so a home that is only a dashboard is answering a
 * different question from the one a person opened it with. Both halves were already here; which one
 * a reader meets first is what this component decides.
 *
 * `data` being unset is a state, not an omission: the surface draws the loading shape — four
 * placeholder tiles and three "Loading…" panels — so the page has its real proportions before the
 * figures land and does not reflow under the reader. Both states are examples below.
 *
 * THE FIXTURE'S ARITHMETIC, kept from the hand-written example this replaced, because a reader is
 * learning the component from these numbers and a figure they cannot derive from the data beside it
 * teaches them the surface is showing something it is not:
 *   open orders  6 = 3 draft + 2 in review + 1 scheduled
 *   open value   $124K = 24,000 + 40,000 + 60,000
 *   win rate     80% = 4 won of 5 decided
 */
const docs: ComponentDocs = {
  aliases: ['dashboard', 'home page', 'landing page', 'overview', 'command centre', 'app home'],

  answers: [
    'give an app a landing page instead of dropping people into a list',
    'show somebody their own unfinished work, longest-waiting first',
    'show what needs attention across a whole app in one view',
    'show a pipeline broken down by stage with what each stage is worth',
    'show the records that moved most recently',
    'give somebody a way into the assistant from where they land',
  ],

  // An app's LANDING surface — the whole page, not a block placed on one. It is not a component you
  // build a home page out of; it IS the home page.
  placements: ['homePage'],

  inputs: [
    {
      name: 'data',
      // No attribute: this is the whole surface's content — tiles, stages, win/loss and activity
      // rows. Absent (or null) is a real state and draws the loading shape rather than an error.
      type: 'json',
      description:
        'Everything the surface shows: `pending` (the lead region — what is waiting on the reader), ' +
        '`kpis` (the headline tiles, each with a figure already formatted and an optional deep ' +
        'link), `pipeline` (the breakdown panel’s stages and its won/lost footer, or null when this ' +
        'workspace has no lifecycle to break down), and `recent` (the newest records with their ' +
        'status). Leave it unset while the read is in flight — that draws the loading state, not an ' +
        'empty one. `pending` is a tagged answer rather than a list that might be empty: `ok` with ' +
        'its `items`, `ok` with none (the reader genuinely has nothing outstanding), `unreadable` ' +
        '(the read failed — say so rather than drawing "nothing is waiting on you"), or ' +
        '`no-lifecycle` (nothing in this workspace keeps a status, so the question does not arise).',
    },
    {
      name: 'userRole',
      attribute: 'user-role',
      type: 'text',
      description:
        'The role named in the hero kicker, after "Overview ·". The hero greets by role, not by name, ' +
        'so the surface takes no name.',
      default: 'Admin',
    },
  ],

  events: [
    {
      name: 'home-ask',
      when: 'The "Ask …" button in the hero is pressed. Opening the assistant is the host’s job.',
      carries: 'nothing',
    },
    {
      name: 'home-navigate',
      when:
        'A tile, an activity row or a panel’s "see them all" link is chosen. Only elements given an ' +
        'href are drawn as controls at all, so a figure with nowhere to go cannot be pressed.',
      carries: '{ href } — where the host was asked to go.',
    },
  ],

  // Genuinely none: the surface renders no <slot>. Everything on it comes from `data`, which is the
  // point — a landing surface a package can supply figures to without supplying markup.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-accent', controls: 'The accented subject word in the headline and the stage bars.' },
    { token: '--caos-color-accent-fill', controls: 'The Ask button, and --caos-color-accent-fill-hover under the pointer.' },
    { token: '--caos-color-accent-contrast', controls: 'The label on the Ask button. White by default; a brand with a pale accent sets a darker ink here.' },
    { token: '--caos-color-bg', controls: 'The ground the surface is drawn on.' },
    { token: '--caos-color-surface', controls: 'The fill of the tiles and the panels.' },
    { token: '--caos-color-border', controls: 'The edges of the tiles, the panels and the activity rows.' },
    { token: '--caos-text-kpi', controls: 'The size of the headline figure on a tile.' },
    { token: '--caos-font-display', controls: 'The typeface of the hero headline and the figures.' },
    { token: '--caos-font-mono', controls: 'The typeface of the kicker and the panel links.' },
    { token: '--caos-color-success', controls: 'The activity dot for a success-toned status, and the Won figure.' },
    { token: '--caos-color-warning', controls: 'The activity dot for a warning-toned status.' },
    { token: '--caos-color-info', controls: 'The activity dot for an info-toned status.' },
    { token: '--caos-color-danger', controls: 'The activity dot for a danger-toned status, and the Lost figure.' },
    {
      token: '--brand-ai-app-name',
      controls:
        'What the hero button offers to ask. Read in script rather than from the stylesheet, and ' +
        're-read live when `data-brand` changes on the document. Unset ⇒ the name the platform ' +
        'ships in its own token floor, so a deployment that customises nothing still reads. ' +
        'EMPTY ⇒ “Ask the assistant”: this one labels a button, and a button with no name cannot ' +
        'be identified by sight or by a screen reader, so a cleared name degrades to what the ' +
        'button DOES rather than putting the platform’s own brand back on a surface an org has ' +
        'deliberately unbranded.',
    },
    {
      token: '--brand-wordmark',
      controls:
        'The accented subject word in the headline — the name of the deployment. Read the same way, ' +
        'rendered exactly as the token carries it — no re-casing, so it reads the same here as it ' +
        'does everywhere else the token is drawn. Unset ⇒ the wordmark the platform ships in its ' +
        'own token floor. EMPTY ⇒ no company name anywhere in the hero, the same answer the topbar ' +
        'gives: an org may clear its Company Name and the surface must let it. The headline is a ' +
        'sentence, so the clause goes with the name rather than leaving a gap after it — it reads ' +
        '“What needs your attention.” and names nobody.',
    },
  ],

  // No named seams. Nothing in the shadow tree carries a `part`, so neither the hero, the tiles nor the
  // panels can be reached from an outside stylesheet.
  parts: [],

  methods: [],

  placing:
    'On its own, as the whole of an app’s home page. It owns the page gutter — 48px, stepping down at ' +
    '1100px and 720px — so the shell’s content padding must be zeroed for the surface holding it, or ' +
    'the two gutters add up. Nothing is placed beside it and nothing is placed inside it. What the ' +
    'host still owns is the two things it reports: opening the assistant, and navigating when a tile ' +
    'or a row is pressed.',

  whenToUse:
    'An app should open on something that says what needs attention rather than on a list a person has ' +
    'to interpret. Reach for it when there is more than one object worth watching and a person’s first ' +
    'question is "what changed and what is open" rather than "find me this record".',

  insteadUse: [
    {
      name: 'PageHeader',
      when:
        'You are heading an ordinary surface rather than replacing it. That is a header you build a ' +
        'page under; this is the page.',
    },
    {
      name: 'StatTile',
      when:
        'You want ONE figure, placed on a page you are assembling yourself. This surface draws its own ' +
        'tiles from its data and gives you nowhere to put anything else.',
    },
    {
      name: 'DataTable',
      when:
        'The landing surface should be the records themselves — an app with one object and no ' +
        'lifecycle worth summarising is better served by its list.',
    },
    {
      name: 'KanbanBoard',
      when:
        'The pipeline is the thing being worked rather than being reported. That one is a place to move ' +
        'records between stages; the breakdown panel here only says how much sits in each.',
    },
  ],

  accessibility:
    'Everything that leads somewhere is drawn as a real button and everything that does not is drawn as ' +
    'plain text, so a keyboard reaches exactly the things that can be pressed and nothing that cannot — ' +
    'a tile given no href is not focusable. The panel headings are real `<h2>`s under the hero’s `<h1>`, ' +
    'so the surface has a document outline. Left to the author: the figures arrive already worded, so ' +
    'their wording IS what is announced — "6" alone reads as nothing, "$124K open" reads as something. ' +
    'The activity status is a coloured dot with its label beside it, and the label is what carries the ' +
    'meaning, so a status supplied with an empty label leaves colour as the only signal.',

  examples: [
    {
      key: 'live',
      label: 'The landing surface',
      summary:
        'The whole surface with live figures — tiles, a $-weighted breakdown with its won/lost footer, ' +
        'and the records that moved most recently.',
      placement: {
        component: 'caos-app-home',
        inputs: {
          'user-role': 'Manager',
          data: {
            pending: {
              status: 'ok',
              title: 'Waiting on you',
              items: [
                {
                  id: 'p9',
                  label: 'PRO-2210 · Harlow Marine',
                  objectLabel: 'Proposal',
                  statusLabel: 'Presented',
                  tone: 'info',
                  waitingLabel: 'Updated 3 months ago',
                  href: '/app/proposals/p9',
                },
                {
                  id: 'o7',
                  label: 'ORD-4460 · Pike & Sons',
                  objectLabel: 'Order',
                  statusLabel: 'Activated',
                  tone: 'warning',
                  waitingLabel: 'Updated 12 days ago',
                  href: '/app/orders/o7',
                },
                {
                  id: 'p4',
                  label: 'PRO-2231 · Ashcroft Group',
                  objectLabel: 'Proposal',
                  statusLabel: 'Draft',
                  tone: 'muted',
                  waitingLabel: 'Updated yesterday',
                  href: '/app/proposals/p4',
                },
              ],
            },
            kpis: [
              { label: 'Open orders', value: '6', sub: '$124K open', href: '/app/orders' },
              { label: 'Won this quarter', value: '$86.9K', sub: '4 won · 1 lost' },
              { label: 'Win rate', value: '80%', sub: '4 of 5 decided' },
            ],
            pipeline: {
              title: 'Pipeline by stage',
              linkLabel: 'All orders',
              linkHref: '/app/orders',
              stages: [
                { status: 'draft', label: 'Draft', count: 3, value: 24000 },
                { status: 'review', label: 'In review', count: 2, value: 40000 },
                { status: 'scheduled', label: 'Scheduled', count: 1, value: 60000 },
              ],
              winLoss: { won: { count: 4, value: 86900 }, lost: { count: 1, value: 12400 } },
            },
            recent: {
              title: 'Recent activity',
              rows: [
                {
                  id: 'o1',
                  label: 'ORD-4471 · Meridian Supply',
                  sub: 'Updated 2 hours ago',
                  status: 'review',
                  statusLabel: 'In review',
                  tone: 'info',
                  href: '/app/orders/o1',
                },
                {
                  id: 'o2',
                  label: 'ORD-4472 · Calder & Finch',
                  sub: 'Updated yesterday',
                  status: 'won',
                  statusLabel: 'Won',
                  tone: 'success',
                  href: '/app/orders/o2',
                },
              ],
            },
          },
        },
      },
      host: {
        summary:
          'The page around it owns navigation: it listens for the two events this surface raises and ' +
          'reports what it was asked to do.',
        run: (stage: HTMLElement) => {
          const surface = stage.querySelector('caos-app-home');
          if (!surface) return;
          const note = document.createElement('p');
          note.textContent =
            'Press a tile, an activity row, a panel link or the Ask button — what the page was asked to do is reported here.';
          stage.append(note);
          surface.addEventListener('home-ask', () => {
            note.textContent = 'The page was asked to open the assistant.';
          });
          surface.addEventListener('home-navigate', (event: Event) => {
            const { href } = (event as CustomEvent<{ href: string }>).detail;
            note.textContent = `The page was asked to navigate to ${href}.`;
          });
        },
      },
    },
    {
      key: 'loading',
      label: 'Before the figures land',
      summary:
        'The same surface with no data set — four placeholder tiles and three waiting panels, holding ' +
        'the page’s real proportions so nothing moves under the reader when the figures arrive.',
      placement: { component: 'caos-app-home', inputs: { 'user-role': 'Manager' } },
    },
    {
      key: 'nothing-to-measure',
      label: 'Nothing on this workspace to measure',
      summary:
        'The read settled and genuinely found no object worth a tile — no app placed here declares a ' +
        'lifecycle. This is the shape a workspace with no sales-shaped (or any status-bearing) app ' +
        'installed actually gets: a real page saying so, never an empty grid of zeroed sales tiles.',
      placement: {
        component: 'caos-app-home',
        inputs: {
          'user-role': 'Manager',
          data: {
            pending: { status: 'no-lifecycle' },
            kpis: [],
            pipeline: null,
            recent: { title: 'Recent activity', rows: [] },
          },
        },
      },
    },
    {
      key: 'nothing-waiting',
      label: 'A clear desk',
      summary:
        'The workspace does keep a lifecycle and the read succeeded — the reader simply has nothing ' +
        'outstanding. This is a real answer and says so, which is why `pending` is a tagged state ' +
        'rather than a list: an empty list and a failed read would otherwise draw the same page.',
      placement: {
        component: 'caos-app-home',
        inputs: {
          'user-role': 'Manager',
          data: {
            pending: { status: 'ok', title: 'Waiting on you', items: [] },
            kpis: [{ label: 'Open orders', value: '0', sub: 'nothing open' }],
            pipeline: null,
            recent: { title: 'Recent activity', rows: [] },
          },
        },
      },
    },
    {
      key: 'pending-unreadable',
      label: 'The waiting list could not be read',
      summary:
        'The figures landed and the reader’s own outstanding work did not. The surface says that ' +
        'rather than drawing an empty list, because "nothing is waiting on you" is the most ' +
        'confident way this page could be wrong.',
      placement: {
        component: 'caos-app-home',
        inputs: {
          'user-role': 'Manager',
          data: {
            pending: { status: 'unreadable' },
            kpis: [{ label: 'Open orders', value: '6', sub: '$124K open' }],
            pipeline: null,
            recent: { title: 'Recent activity', rows: [] },
          },
        },
      },
    },
  ],
};

export default docs;
